import { isMarketOrderType, type OrderTxnType } from "domain/synthetics/orders";

type CompletionOrder = {
  txnType: OrderTxnType;
  orderType: number;
};

type CompletionStatus = {
  createdTxnHash?: string;
  updatedTxnHash?: string;
  cancelledTxnHash?: string;
  executedTxnHash?: string;
};

type CancellationEventData = {
  stringItems?: {
    items?: {
      reason?: string;
    };
  };
  bytesItems?: {
    items?: {
      reasonBytes?: string;
    };
  };
};

const PANIC_SELECTOR = "0x4e487b71";
const PANIC_REASONS: Record<string, string> = {
  "11": "Arithmetic overflow or underflow",
  "12": "Division or modulo by zero",
  "21": "Invalid enum conversion",
  "31": "Empty array pop",
  "32": "Array index out of bounds",
  "41": "Memory allocation overflow",
  "51": "Call to an uninitialized function",
};

export function decodeCancellationReasonBytes(reasonBytes: string) {
  if (!reasonBytes.toLowerCase().startsWith(PANIC_SELECTOR) || reasonBytes.length < 10 + 64) {
    return undefined;
  }

  try {
    const panicCode = BigInt(`0x${reasonBytes.slice(10)}`);
    const code = panicCode.toString(16).padStart(2, "0");
    const description = PANIC_REASONS[code] ?? "Solidity panic";
    return `${description} (panic 0x${code})`;
  } catch (_error) {
    return undefined;
  }
}

export function isPendingOrderStatusCompleted(order: CompletionOrder, orderStatus?: CompletionStatus) {
  if (order.txnType === "create") {
    return isMarketOrderType(order.orderType)
      ? Boolean(orderStatus?.executedTxnHash || orderStatus?.cancelledTxnHash)
      : Boolean(orderStatus?.createdTxnHash);
  }

  if (order.txnType === "update") {
    return Boolean(orderStatus?.updatedTxnHash);
  }

  if (order.txnType === "cancel") {
    return Boolean(orderStatus?.cancelledTxnHash);
  }

  return false;
}

export function getCancellationReasonFromEventData(eventData: CancellationEventData) {
  const reasonBytes = eventData.bytesItems?.items?.reasonBytes?.trim();
  if (reasonBytes && reasonBytes !== "0x") {
    const decodedReason = decodeCancellationReasonBytes(reasonBytes);
    if (decodedReason) return decodedReason;
  }

  const reason = eventData.stringItems?.items?.reason?.trim();
  if (reason) return reason;

  if (reasonBytes && reasonBytes !== "0x") return reasonBytes;

  return undefined;
}
